import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { CaretDown, CaretUp, Globe, Prohibit } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * IP Origin Timeline
 * Shows every customer account created from the same registration IP as the
 * currently-viewed customer, ordered oldest → newest. Each entry gets a
 * "1st origin / 2nd origin / …" label and its creation timestamp so admin can
 * quickly identify which was the original account and which are duplicates.
 *
 * Only renders when there is more than one sibling (otherwise there is no
 * duplication to show).
 */
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const IPOriginTimeline = ({ customerId, currentCustomerName }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [blocking, setBlocking] = useState(false);
  const { user } = useAuth();

  const reload = () => {
    if (!customerId) return;
    setLoading(true);
    axios.get(`${API}/customers/${customerId}/ip-siblings`)
      .then((res) => setData(res.data))
      .catch(() => setData({ ip_address: null, siblings: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [customerId]);

  const doBulkBlock = async () => {
    if (!window.confirm(
      `Block every OTHER account sharing this IP with "${currentCustomerName || 'this customer'}"?\n\n` +
      `This will block ${(data?.siblings || []).length - 1} account(s) and kick them out of active sessions. This cannot be undone quickly.`
    )) return;
    setBlocking(true);
    try {
      const res = await axios.post(`${API}/customers/bulk-block-by-ip`, {
        admin_id: user?.id,
        seed_customer_id: customerId,  // exclude self by default
      });
      toast.success(`✅ Blocked ${res.data.blocked_count} duplicate account(s)`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to bulk-block');
    } finally { setBlocking(false); }
  };

  if (loading || !data) return null;
  const siblings = data.siblings || [];
  if (siblings.length <= 1) return null;  // Nothing to show for unique IPs

  return (
    <div className="bg-white mt-2 p-4" data-testid="ip-origin-timeline">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid="ip-origin-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <Globe size={16} weight="fill" className="text-red-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              IP Origin Timeline
              <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-red-500 text-white rounded-sm">
                D · {siblings.length} IDs
              </span>
            </h3>
            <p className="text-xs text-gray-500">
              Accounts created from {data.ip_address || 'this IP'}
            </p>
          </div>
        </div>
        {open ? <CaretUp size={16} className="text-gray-400" /> : <CaretDown size={16} className="text-gray-400" />}
      </button>

      {open && (
        <div className="mt-3 relative">
          {/* Admin-only bulk block button — blocks every duplicate except this one */}
          {user?.role === 'super_admin' && siblings.length > 1 && (
            <button
              onClick={doBulkBlock}
              disabled={blocking}
              className="mb-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-bold shadow-sm disabled:opacity-50"
              data-testid="ip-bulk-block-btn"
              title="Block every other account sharing this IP"
            >
              <Prohibit size={14} weight="bold" />
              {blocking ? 'Blocking…' : `Block ${siblings.length - 1} other account(s)`}
            </button>
          )}
          {/* Vertical timeline line */}
          <div className="absolute left-3 top-1 bottom-1 w-0.5 bg-gradient-to-b from-red-200 via-gray-200 to-gray-200" />
          <div className="space-y-2">
            {siblings.map((s) => {
              const isCurrent = s.is_current;
              const displayName = s.personal_name || s.username || 'Unknown';
              const created = s.created_at
                ? new Date(s.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata',
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })
                : 'Unknown time';
              return (
                <div
                  key={s.id}
                  className={`relative pl-8 pr-3 py-2 rounded-lg border ${
                    isCurrent
                      ? 'border-red-300 bg-red-50/60'
                      : 'border-gray-200 bg-gray-50/40'
                  }`}
                  data-testid={`ip-sibling-${s.id}`}
                >
                  {/* Origin index dot */}
                  <div
                    className={`absolute left-1 top-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                      isCurrent ? 'bg-red-500' : 'bg-gray-400'
                    }`}
                  >
                    {s.origin_index}
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate flex items-center gap-1.5">
                        {displayName}
                        {isCurrent && (
                          <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1 py-0.5 rounded">
                            This account
                          </span>
                        )}
                        {s.is_blocked && (
                          <span className="text-[9px] font-bold text-white bg-red-500 px-1 py-0.5 rounded">
                            Blocked
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {s.phone || '—'}
                        {s.mb_setup ? <span className="ml-1.5 px-1 bg-purple-100 text-purple-700 rounded text-[9px]">{s.mb_setup}</span> : null}
                        {s.serial_number ? <span className="ml-1.5 text-gray-400">Code: {s.serial_number}</span> : null}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        <span className="font-semibold">{ordinal(s.origin_index)} origin</span> · {created}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default IPOriginTimeline;
